<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import { useFieldSave } from '@/composables/useFieldSave'
import { useSyncedModel } from '@/composables/useSyncedModel'
import type { FieldStateMap } from '@/utils/field-state'
import CompaniesController from '@controllers/Investors/CompaniesController'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CompanyData = App.Data.Company.CompanyData

const props = defineProps<{
  fieldStates: FieldStateMap
  company: CompanyData
}>()

const { t } = useI18n()

const masterFields = computed<InlineEditableField[]>(() => [
  { key: 'name', label: t('fields.name'), type: 'text', fullWidth: true },
  { key: 'address', label: t('fields.address'), type: 'text', fullWidth: true },
  { key: 'managing_director', label: t('fields.managing_director'), type: 'text' },
  {
    key: 'commercial_registration_number',
    label: t('fields.commercial_registration_number'),
    type: 'text',
  },
  { key: 'registration_court', label: t('fields.registration_court'), type: 'text', fullWidth: true },
  { key: 'date_of_establishment', label: t('fields.date_of_establishment'), type: 'date' },
  { key: 'purpose', label: t('fields.purpose'), type: 'textarea', fullWidth: true },
])

const contactFields = computed<InlineEditableField[]>(() => [
  { key: 'email', label: t('fields.email'), type: 'text', fullWidth: true },
  { key: 'website', label: t('fields.website'), type: 'text', fullWidth: true },
  { key: 'telephone', label: t('fields.telephone'), type: 'text' },
])

const editableKeys = computed(() => [...masterFields.value, ...contactFields.value].map((field) => field.key))

const seedModel = (): InlineEditableModel => {
  const source = props.company as unknown as Record<string, InlineEditableValue>
  return Object.fromEntries(editableKeys.value.map((key) => [key, source[key] ?? null]))
}

const { model: companyModel, confirm } = useSyncedModel(seedModel)

const update = useFieldSave({
  url: CompaniesController.update(props.company.id ?? '').url,
  reloadProps: ['company', 'fieldStates', 'companyCompletion'],
  onSaved: confirm,
})

const save = ({ key, value }: InlineEditableCommitPayload) => update({ [key]: value })
</script>

<template>
  <div class="grid items-start gap-4 lg:grid-cols-2">
    <FinestCard :title="t('cards.master')" icon="pi pi-building">
      <FinestFieldGrid v-model="companyModel" :fields="masterFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>

    <FinestCard :title="t('cards.contact')" icon="pi pi-envelope">
      <FinestFieldGrid v-model="companyModel" :fields="contactFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "cards": {
      "master": "Stammdaten",
      "contact": "Kontaktdaten"
    },
    "fields": {
      "name": "Firmenname",
      "address": "Adresse",
      "managing_director": "Geschäftsführer",
      "commercial_registration_number": "Handelsregisternummer",
      "registration_court": "Registergericht",
      "date_of_establishment": "Gründungsdatum",
      "purpose": "Unternehmenszweck",
      "email": "E-Mail",
      "website": "Webseite",
      "telephone": "Telefon"
    }
  },
  "en": {
    "cards": {
      "master": "Master data",
      "contact": "Contact data"
    },
    "fields": {
      "name": "Company name",
      "address": "Address",
      "managing_director": "Managing director",
      "commercial_registration_number": "Commercial register number",
      "registration_court": "Register court",
      "date_of_establishment": "Date of establishment",
      "purpose": "Company purpose",
      "email": "Email",
      "website": "Website",
      "telephone": "Phone"
    }
  }
}
</i18n>
